import { defineStore } from 'pinia'
import * as sdk from '@/helpers/sdk'

import useProfileStore, { profileKey } from '@/stores/profile.store'
import useRouterStore from '@/stores/router.store'
import profileQueue from '@/helpers/profileQueue'
import routeNames from '@/router/names'
import getRedirectAfterDelete from '@/helpers/profile/getRedirectAfterDelete'
import useAction from '@/composables/useAction'

const meta = {
  requestPermission: {
    read: true,
    edit: true,
  },
}

async function createGlobalPresetRange({ ...payload } = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    channel,
    presetRanges,
  } = profileStore // current

  let newOrder = 0
  if (typeof payload._order === 'number') {
    newOrder = payload._order
  } else if (presetRanges) {
    newOrder = presetRanges.length
  }

  const result = await profileQueue.add(
    () => sdk.v3.profiles.global.createPresetRange({
      url: {
        profileUid: profile.uid,
        cUid: channel.data._uid,
      },
      body: payload,
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect based on index, to select the newly created presetRange
  await routerStore.push({
    name: routeNames.GlobalPresetRange,
    params: {
      profileUid: profileStore.profileUid,
      channelUid: profileStore.channelUid,
      presetRangeUid: newOrder,
    },
  })

  return result.data
}

async function createLocalPresetRange({ ...payload } = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    mode,
    channel,
    presetRanges,
  } = profileStore // current

  let newOrder = 0
  if (typeof payload._order === 'number') {
    newOrder = payload._order
  } else if (presetRanges) {
    newOrder = presetRanges.length
  }

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.createPresetRange({
      url: {
        profileUid: profile.uid,
        mUid: mode.data._uid,
        cUid: channel.data._uid,
      },
      body: payload,
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect based on index, to select the newly created presetRange
  await routerStore.push({
    name: routeNames.LocalPresetRange,
    params: {
      profileUid: profileStore.profileUid,
      modeUid: profileStore.modeUid,
      channelUid: profileStore.channelUid,
      presetRangeUid: newOrder,
    },
  })

  return result.data
}

async function duplicateGlobalPresetRange({ _uid } = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    channel,
    presetRange,
  } = profileStore // current

  const newOrder = presetRange.data._order + 1

  const result = await profileQueue.add(
    () => sdk.v3.profiles.global.copyPresetRange({
      url: {
        profileUid: profile.uid,
        cUid: channel.data._uid,
        prUid: (
          _uid || presetRange.data._uid
        ),
      },
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect based on index, to select the newly created presetRange
  await routerStore.push({
    name: routeNames.GlobalPresetRange,
    params: {
      profileUid: profileStore.profileUid,
      channelUid: profileStore.channelUid,
      presetRangeUid: newOrder,
    },
  })

  return result.data
}

async function duplicateLocalPresetRange({ _uid } = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    mode,
    channel,
    presetRange,
  } = profileStore // current

  const newOrder = presetRange.data._order + 1

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.copyPresetRange({
      url: {
        profileUid: profile.uid,
        mUid: mode.data._uid,
        cUid: channel.data._uid,
        prUid: (
          _uid || presetRange.data._uid
        ),
      },
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect based on index, to select the newly created presetRange
  await routerStore.push({
    name: routeNames.LocalPresetRange,
    params: {
      profileUid: profileStore.profileUid,
      modeUid: profileStore.modeUid,
      channelUid: profileStore.channelUid,
      presetRangeUid: newOrder,
    },
  })

  return result.data
}

async function deleteGlobalPresetRange({ _uid } = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    channel,
    presetRange,
  } = profileStore // current

  const redirectTo = getRedirectAfterDelete(presetRange)

  const result = await profileQueue.add(
    () => sdk.v3.profiles.global.deletePresetRange({
      url: {
        profileUid: profile.uid,
        cUid: channel.data._uid,
        prUid: (
          _uid || presetRange.data._uid
        ),
      },
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect to closest presetRange
  await routerStore.push(redirectTo)

  return result.data
}

async function deleteLocalPresetRange({ _uid } = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    mode,
    channel,
    presetRange,
  } = profileStore // current

  const redirectTo = getRedirectAfterDelete(presetRange)

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.deletePresetRange({
      url: {
        profileUid: profile.uid,
        mUid: mode.data._uid,
        cUid: channel.data._uid,
        prUid: (
          _uid || presetRange.data._uid
        ),
      },
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect to closest presetRange
  await routerStore.push(redirectTo)

  return result.data
}

async function distributeLocalPresetRange({ _uid, ...payload } = {}) {
  const profileStore = useProfileStore()

  const profile = profileStore[profileKey]

  const {
    mode,
    channel,
    presetRange,
  } = profileStore // current

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.distributePresets({
      url: {
        profileUid: profile.uid,
        mUid: mode.data._uid,
        cUid: channel.data._uid,
        prUid: (
          _uid || presetRange.data._uid
        ),
      },
      body: payload,
    }),
  ).result

  profileStore[profileKey] = result.data

  return result.data
}

async function distributeGlobalPresetRange({ _uid, ...payload }) {
  const profileStore = useProfileStore()

  const profile = profileStore[profileKey]
  const {
    globalChannel,
    globalPresetRange,
  } = profileStore

  const result = await profileQueue.add(
    () => sdk.v3.profiles.global.distributePresets({
      url: {
        profileUid: profile.uid,
        cUid: globalChannel.data._uid,
        prUid: (
          _uid || globalPresetRange.data._uid
        ),
      },
      body: payload,
    }),
  ).result

  profileStore[profileKey] = result.data
  return result.data
}

async function moveLocalPreset({ _uid, _order }) {
  const profileStore = useProfileStore()

  const profile = profileStore[profileKey]
  const {
    mode,
    channel,
    presetRange,
    preset,
  } = profileStore

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.reorderPreset({
      url: {
        profileUid: profile.uid,
        mUid: mode.data._uid,
        cUid: channel.data._uid,
        prUid: presetRange.data._uid,
        pUid: (
          _uid || preset.data._uid
        ),
      },
      body: { _order },
    }),
  ).result

  profileStore[profileKey] = result.data

  return result.data
}

async function moveGlobalPreset({ _uid, _order }) {
  const profileStore = useProfileStore()

  const profile = profileStore[profileKey]
  const {
    globalChannel,
    globalPresetRange,
    globalPreset,
  } = profileStore

  const result = await profileQueue.add(
    () => sdk.v3.profiles.global.reorderPreset({
      url: {
        profileUid: profile.uid,
        cUid: globalChannel.data._uid,
        prUid: globalPresetRange.data._uid,
        pUid: (
          _uid || globalPreset.data._uid
        ),
      },
      body: { _order },
    }),
  ).result

  profileStore[profileKey] = result.data
  return result.data
}

const usePresetRangeStore = defineStore('profile/presetRange', () => {
  const { action: createGlobal, status } = useAction(createGlobalPresetRange, { meta })

  return {
    status,
    createGlobal,
    createLocal: useAction(createLocalPresetRange, { status, meta }).action,
    duplicateGlobal: useAction(duplicateGlobalPresetRange, { status, meta }).action,
    duplicateLocal: useAction(duplicateLocalPresetRange, { status, meta }).action,
    deleteGlobal: useAction(deleteGlobalPresetRange, { status, meta }).action,
    deleteLocal: useAction(deleteLocalPresetRange, { status, meta }).action,
    distributeLocal: useAction(distributeLocalPresetRange, { status, meta }).action,
    distributeGlobal: useAction(distributeGlobalPresetRange, { status, meta }).action,
    moveLocalPreset: useAction(moveLocalPreset, { status, meta }).action,
    moveGlobalPreset: useAction(moveGlobalPreset, { status, meta }).action,
  }
})

export default usePresetRangeStore
